import toast from 'react-hot-toast';
import i18n from '~/i18n';
import { unitPriceMaxDecimals } from './unitPrice';
import type {
  Money,
  OrderStatus,
  Quantity,
  UnitCode,
  UserRole,
} from '~/graphql/graphql.ts';
import { SellerProductConfigurationScope } from '~/graphql/graphql.ts';
import { GraphQLRequestError } from '~/lib/GraphQLRequestError.ts';
import { STATUS_FLOW } from '~/types/orders.ts';
import { downloadFile } from './downloadFile';
import {
  getEffectivePricePerPiece,
  getListedPricePerPiece,
  getListedPricePerUnit,
} from './order';

/**
 * Scope-aware mutation arguments for upserting a seller product configuration.
 * FACILITY configs carry the owning facility id; ORGANIZATION configs carry
 * neither a facility nor a location.
 */
export type SellerProductConfigurationScopeArgs = {
  scope: SellerProductConfigurationScope;
  facilityId?: string;
};

/**
 * Derives scope-aware upsert arguments from an existing configuration so a save
 * edits the same-scoped configuration instead of silently forking a new
 * facility override. Never sends `locationId`.
 *
 * - ORGANIZATION → `{ scope: ORGANIZATION }` (no facilityId, no locationId)
 * - FACILITY → `{ scope: FACILITY, facilityId }` (no locationId)
 */
export function buildScopeArgsFromConfiguration(
  configuration:
    | {
        scope: SellerProductConfigurationScope;
        facility?: { id: string } | null;
      }
    | null
    | undefined
): SellerProductConfigurationScopeArgs {
  // An unconfigured product has no scope yet; default to Facility to match the
  // loaded-scope fallback used across the seller product screens.
  if (configuration?.scope === SellerProductConfigurationScope.Organization) {
    return { scope: SellerProductConfigurationScope.Organization };
  }
  return {
    scope: SellerProductConfigurationScope.Facility,
    facilityId: configuration?.facility?.id,
  };
}

export const cn = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

export const isBuyerCapable = (role: string | null | undefined): boolean =>
  role === 'BUYER' || role === 'TRADER';

export const isSellerCapable = (role: string | null | undefined): boolean =>
  role === 'SELLER' || role === 'TRADER';

type LocationDisplayInput = {
  name: string;
  facility?: {
    address?: {
      addressLine1?: string | null;
      city?: string | null;
      region?: string | null;
    } | null;
  } | null;
};

/** Canonical location label used across the app: "Name - addressLine1, city, region". */
export function formatLocationName(
  location: LocationDisplayInput | null | undefined
): string {
  if (!location) return '';
  const address = location.facility?.address;
  const addressParts = [
    address?.addressLine1,
    address?.city,
    address?.region,
  ].filter((part): part is string => Boolean(part?.trim()));
  const parts = [location.name, addressParts.join(', ')].filter(
    (part): part is string => Boolean(part?.trim())
  );
  return parts.join(' - ').trim();
}

export function getLocalDateString(date: Date = new Date()): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

export function formatDate(dateString: string, locale?: string) {
  const currentLocale = locale || i18n.language || 'en-US';

  return new Date(dateString).toLocaleDateString(currentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Local midnight, because a bare YYYY-MM-DD parses as UTC and renders as the
// previous day in any negative-offset locale.
export function formatDateOnly(dateOnlyString: string, locale?: string) {
  return formatDate(`${dateOnlyString}T00:00:00`, locale);
}

export function formatDateTime(dateString: string, locale?: string) {
  const currentLocale = locale || i18n.language || 'en-US';
  return new Date(dateString).toLocaleString(currentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Intentionally narrower than i18next's TFunction — only the subset used here.
type TFunction = (key: string, options?: Record<string, unknown>) => string;

export function formatRelativeTime(dateString: string, t?: TFunction): string {
  const parsed = new Date(dateString);
  if (isNaN(parsed.getTime())) return '—';
  const diff = Date.now() - parsed.getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (t) {
    if (days >= 1)
      return days === 1
        ? t('dashboard.relative_time.day_ago')
        : t('dashboard.relative_time.days_ago', { count: days });
    if (hours >= 1)
      return hours === 1
        ? t('dashboard.relative_time.hour_ago')
        : t('dashboard.relative_time.hours_ago', { count: hours });
    if (minutes >= 1)
      return minutes === 1
        ? t('dashboard.relative_time.minute_ago')
        : t('dashboard.relative_time.minutes_ago', { count: minutes });
    return t('dashboard.relative_time.just_now');
  }

  if (days >= 1) {
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }
  if (hours >= 1) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  if (minutes >= 1) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  }
  return 'just now';
}

// Intl.NumberFormat construction is slow, so cache one instance per locale + options.
const numberFormatCache = new Map<string, Intl.NumberFormat>();
function numberFormatFor(
  locale: string,
  options: Intl.NumberFormatOptions
): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = numberFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    numberFormatCache.set(key, formatter);
  }
  return formatter;
}

export function formatPercent(fraction: number, locale?: string): string {
  const options: Intl.NumberFormatOptions = {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  };
  try {
    return numberFormatFor(locale || i18n.language || 'en-US', options).format(
      fraction
    );
  } catch {
    return numberFormatFor('en-US', options).format(fraction);
  }
}

/**
 * Formats a currency amount with a configurable number of decimal places.
 * Used directly for hover/tooltip displays (6dp) and as the implementation
 * backing the standard 2dp `formatCurrency`.
 */
export function formatCurrencyFull(
  amount: string | number | undefined | null,
  decimals = 6,
  currency = 'USD',
  locale?: string,
  maxDecimals = decimals
): string {
  if (amount === undefined || amount === null) return '';
  const numericAmount =
    typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return '';
  const currentLocale = locale || i18n.language || 'en-US';
  try {
    return numberFormatFor(currentLocale, {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: maxDecimals,
    }).format(numericAmount);
  } catch (e) {
    // currency isn't a well-formed ISO 4217 code (wrong length, non-letters, empty):
    // Intl.NumberFormat throws a RangeError at construction instead of degrading
    // gracefully the way it does for a well-formed-but-unregistered code (e.g. "ZZZ"
    // renders as "ZZZ 100.00" with no symbol, no throw). Money.currency has no
    // format validation backend-side, so this is reachable from bad data, not just
    // theoretical.
    if (!(e instanceof RangeError)) throw e;
    return 'N/A';
  }
}

export function formatCurrency(
  amount: string | number | undefined | null,
  currency = 'USD',
  locale?: string
): string {
  return formatCurrencyFull(amount, 2, currency, locale);
}

export function getCurrencySymbol(currency = 'USD', locale?: string): string {
  const currentLocale = locale || i18n.language || 'en-US';
  try {
    const parts = numberFormatFor(currentLocale, {
      style: 'currency',
      currency,
    }).formatToParts(0);
    return parts.find((part) => part.type === 'currency')?.value ?? currency;
  } catch (e) {
    if (!(e instanceof RangeError)) throw e;
    return currency;
  }
}

/**
 * Renders an order total that may be unknown: returns `naText` (a localized
 * "N/A") when `total` is null or `currency` is missing, otherwise the
 * formatted currency amount. Requiring currency avoids formatCurrency's own
 * USD default silently mislabeling a non-USD amount when the caller couldn't
 * determine a currency (e.g. an order with no priced items yet).
 */
export function formatOrderTotal(
  total: number | null,
  naText: string,
  currency?: string
): string {
  return total == null || currency == null
    ? naText
    : formatCurrency(total, currency);
}

/** Returns the singular unit label used for price-per-unit displays.
 * "PCS" → "PC" (per piece), all other units unchanged. */
export function priceUnitLabel(unit: string): string {
  return unit === 'PCS' ? 'PC' : unit;
}

export function formatMoney(
  money: Money | null | undefined,
  locale?: string
): string {
  if (!money) return '';
  return formatCurrency(money.amount, money.currency, locale);
}

/** Rounds a price-per-piece amount to 6 decimal places (sub-cent precision). */
export function roundTo6dp(amount: number): number {
  return Math.round(amount * 1_000_000) / 1_000_000;
}

// Both sides round to the target's precision so a price just over a sub-dollar target cannot
// round up to a whole dollar and read as within it.
export function isWithinTarget(price: number, target: number): boolean {
  const factor = 10 ** unitPriceMaxDecimals(target);
  return Math.round(price * factor) <= Math.round(target * factor);
}

export function formatUnitPrice(
  amount: number,
  currency = 'USD',
  locale?: string
): string {
  return formatCurrencyFull(
    amount,
    2,
    currency,
    locale,
    unitPriceMaxDecimals(amount)
  );
}

export function formatUnitAmount(amount: number, locale?: string): string {
  return numberFormatFor(locale || i18n.language || 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: unitPriceMaxDecimals(amount),
  }).format(amount);
}

/** Formats a number with up to `maximumFractionDigits` decimals, dropping trailing zeros. */
export function formatDecimal(
  amount: number,
  maximumFractionDigits = 2,
  locale?: string
): string {
  return numberFormatFor(locale || i18n.language || 'en-US', {
    maximumFractionDigits,
  }).format(amount);
}

/**
 * Formats a quantity value appropriate for its unit.  PCS quantities are
 * whole numbers (no partial pieces), so they are rounded and shown without
 * decimal places.  All other units keep up to 3 fixed decimal places.
 */
export function formatQuantityForUnit(quantity: {
  value: number;
  unit: UnitCode;
}): string {
  const { value, unit } = quantity;
  if (unit === 'PCS') return Math.round(value).toLocaleString();
  const fixedValue = Number(value.toFixed(3));
  if (value > 0 && fixedValue === 0) return '< 0.001';
  if (value < 0 && fixedValue === 0) return '> -0.001';
  return Number.isInteger(fixedValue)
    ? fixedValue.toString()
    : fixedValue.toFixed(3);
}

export function formatQuantityCell(value: number, unit: UnitCode): string {
  return `${formatQuantityForUnit({ value, unit })} ${unit}`;
}

export function formatNumberWithoutDecimals(
  amount: string | number,
  locale?: string
): string {
  const numericAmount =
    typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) {
    return '0';
  }

  const currentLocale = locale || i18n.language || 'en-US';

  return numberFormatFor(currentLocale, {
    maximumFractionDigits: 0,
  }).format(numericAmount);
}

/** Parses user input as a non-negative integer; returns null for empty, non-numeric, fractional, or negative input. */
export function parseNonNegativeInt(value: unknown): number | null {
  const raw = String(value ?? '').trim();
  const parsed = Number(raw);
  return raw !== '' && Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

/**
 * Parses a price input that may be empty. Returns null for empty / non-finite input — used to
 * distinguish "user cleared the field" (null) from "user typed 0" (0).
 */
export function parseOptionalPrice(raw: unknown): number | null {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return null;
  // Number() (not parseFloat()) so partial-numeric input like "12abc" rejects
  // as null instead of silently truncating to 12.
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Parses a margin percentage input (e.g. "15" -> 0.15). Blank means 0% — margin has
 * no separate clear path, unlike material cost, where a blank input deletes the price
 * row. A malformed non-empty value returns null so the caller can keep the prior margin.
 */
export function parseMarginPercentInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return 0;
  const parsedPercent = parseOptionalPrice(trimmed);
  if (parsedPercent === null) return null;
  return roundTo6dp(parsedPercent / 100);
}

// Created lazily per locale: constructing Intl.DisplayNames at import time
// would crash the whole app in environments missing the API or region data.
const regionDisplayNamesByLocale = new Map<string, Intl.DisplayNames | null>();

function regionDisplayNamesFor(locale: string): Intl.DisplayNames | null {
  if (!regionDisplayNamesByLocale.has(locale)) {
    try {
      regionDisplayNamesByLocale.set(
        locale,
        new Intl.DisplayNames([locale], { type: 'region' })
      );
    } catch {
      regionDisplayNamesByLocale.set(locale, null);
    }
  }
  return regionDisplayNamesByLocale.get(locale) ?? null;
}

/** Maps an ISO-2 country code (e.g. "US") to its display name in the active language (e.g. "United States"), falling back to the raw code. */
function countryDisplayName(
  iso2: string | null | undefined,
  locale?: string
): string {
  if (!iso2) return '';
  const displayNames = regionDisplayNamesFor(locale || i18n.language || 'en');
  if (!displayNames) return iso2;
  try {
    return displayNames.of(iso2) ?? iso2;
  } catch {
    return iso2;
  }
}

/**
 * Seed port names look like "USSAV - Savannah, GA (Seaport)"; strip the
 * UN/LOCODE prefix and facility-type suffix for display. When the port's
 * country name is given (shown separately, e.g. as a group header), a
 * trailing ", Country" is stripped too: "Antwerp, Belgium" -> "Antwerp".
 */
function formatPortName(name: string, countryName?: string): string {
  const stripped = name
    .replace(/^[A-Z0-9]{5} - /, '')
    .replace(/ \(Seaport\)$/, '');
  if (
    countryName &&
    stripped.toLowerCase().endsWith(`, ${countryName.toLowerCase()}`)
  ) {
    return stripped.slice(0, stripped.length - countryName.length - 2);
  }
  return stripped;
}

/**
 * Display parts for a port: the group is the country display name and the
 * label is the cleaned port name without the country (the group shows it):
 * "NLRTM - Rotterdam, South Holland (Seaport)" with country "NL" ->
 * { label: "Rotterdam, South Holland", group: "Netherlands" }.
 */
export function portDisplay(
  port: { name: string; country?: string | null },
  locale?: string
): {
  label: string;
  group: string;
  groupSortKey: string;
} {
  // The seed port names embed English country suffixes and the priority sort
  // keys on English names, so stripping/sorting always use 'en'; only the
  // displayed group follows the active language.
  const englishCountryName = countryDisplayName(port.country, 'en');
  return {
    label: formatPortName(port.name, englishCountryName),
    group: countryDisplayName(port.country, locale),
    groupSortKey: englishCountryName,
  };
}

/** "Global Supplies" + "VENDOR-1" -> "Global Supplies (VENDOR-1)"; no code -> just the name. */
export function formatNameWithCode(name: string, code?: string | null): string {
  const trimmedCode = code?.trim();
  return trimmedCode ? `${name} (${trimmedCode})` : name;
}

export function sellerLabel(organization: {
  name: string;
  sellingOrganizationCode?: string | null;
}): string {
  return formatNameWithCode(
    organization.name,
    organization.sellingOrganizationCode
  );
}

/** "SPF" + "Spruce" -> "SPF — Spruce"; no code -> just the name. */
export function productLabel(product: {
  code?: string | null;
  name: string;
}): string {
  return product.code ? `${product.code} — ${product.name}` : product.name;
}

/**
 * Appends the dimensioning basis to a length label so nominal and actual variants of the same
 * physical length are distinguishable: "12 FT" -> "12 FT - N" (nominal) or "12 FT - A" (actual).
 * A null/undefined basis is treated as actual, matching the backend default.
 */
export function formatLengthWithBasis(
  lengthDisplayValue: string,
  isNominal: boolean | null | undefined
): string {
  return `${lengthDisplayValue} - ${isNominal ? 'N' : 'A'}`;
}

export function formatOrderId(id: string): string {
  return id.slice(0, 13);
}

/** Mirrors the backend's StringUtils.hasText: true when non-null and not
 * empty/whitespace-only. */
export function hasText(value: string | null | undefined): value is string {
  return value != null && value.trim().length > 0;
}

/**
 * Standard order display id: the buyer/seller-facing PO number when set,
 * falling back to the truncated order id (see formatOrderId). Centralizes the
 * `purchaseOrderNumber ?? formatOrderId(id)` fallback so call sites don't each
 * decide independently whether to truncate the id fallback.
 */
export function formatOrderDisplayId(order: {
  purchaseOrderNumber?: string | null;
  id: string;
}): string {
  return hasText(order.purchaseOrderNumber)
    ? order.purchaseOrderNumber
    : formatOrderId(order.id);
}

export function formatOrderStatus(status: string, role: UserRole | undefined) {
  if (role === 'SELLER' && status === 'PENDING_SELLER_CONFIRMATION') {
    return 'Pending Your Confirmation';
  }

  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

/** amount is always required; currency is optional so existing callers (and test
 * fixtures) that only care about the amount don't need to supply it. */
export type PricedAmount = Pick<Money, 'amount'> &
  Partial<Pick<Money, 'currency'>>;

export type OrderItemForTotal = {
  quantity: Pick<Quantity, 'value'>;
  price?: PricedAmount | null;
  effectivePrice?: PricedAmount | null;
  quantityStored?: Pick<Quantity, 'value'> | null;
  pricePerPiece?: PricedAmount | null;
  suggestedPricePerPiece?: PricedAmount | null;
  pickupPrice?: PricedAmount | null;
  deliveryPrice?: PricedAmount | null;
  pickupPricePerPiece?: PricedAmount | null;
  deliveryPricePerPiece?: PricedAmount | null;
  cannedTally?: { id: string } | null;
  tallyPlatformProduct?: { id: string } | null;
};

export const isRandomTallyItem = (
  item: Pick<OrderItemForTotal, 'tallyPlatformProduct' | 'cannedTally'>
) => item.tallyPlatformProduct?.id != null && item.cannedTally == null;

const usesPiecePrice = (
  item: Pick<
    OrderItemForTotal,
    'tallyPlatformProduct' | 'cannedTally' | 'pricePerPiece'
  >
) => !isRandomTallyItem(item) && item.pricePerPiece != null;

export const getLinePiecePrices = (
  item: OrderItemForTotal,
  incoTerm?: string | null
) => ({
  listedPricePerPiece: usesPiecePrice(item)
    ? getListedPricePerPiece(item, incoTerm)
    : null,
  suggestedPerPiece: isRandomTallyItem(item)
    ? null
    : (item.suggestedPricePerPiece ?? null),
});

type OrderForTotal = {
  items: Array<OrderItemForTotal>;
};

export type OrderTotals = {
  listedTotal: number | null;
  suggestedTotal: number | null;
};

export function roundToCents(amount: number): number {
  if (!Number.isFinite(amount)) return amount;
  const cleanedAmount = Number(amount.toPrecision(15));
  const sign = cleanedAmount < 0 ? -1 : 1;
  const digits = String(Math.abs(cleanedAmount));
  if (digits.includes('e'))
    return (sign * Math.round(Math.abs(cleanedAmount) * 100)) / 100;
  return sign * Number(`${Math.round(Number(`${digits}e2`))}e-2`);
}

export function cartLineTotal({
  displayPricePerUnit,
  rawPricePerPiece,
  piecesCount,
  displayQuantity,
}: {
  displayPricePerUnit: number;
  rawPricePerPiece?: number | null;
  piecesCount: number;
  displayQuantity: number;
}): number {
  return rawPricePerPiece != null
    ? roundToCents(rawPricePerPiece * piecesCount)
    : roundToCents(displayPricePerUnit * displayQuantity);
}

/**
 * Sums one total across an order's items, selecting a per-piece field for
 * piece-quantity items and a per-unit field for standard-unit items. Mirrors
 * the backend's `OrderEmailService.sumOrderAmount` pattern: sum at full
 * precision, then round the accumulated total exactly once at the end (never
 * round an intermediate per-unit/per-piece price before multiplying by
 * quantity) — except random-tally line items, which round each line's
 * price × quantity to cents, the same formula the line displays use.
 */
function sumOrderTotal(
  items: OrderItemForTotal[],
  pieceSelector: (
    item: OrderItemForTotal
  ) => Pick<Money, 'amount'> | null | undefined,
  standardSelector: (
    item: OrderItemForTotal
  ) => Pick<Money, 'amount'> | null | undefined
): number | null {
  let rawSum = 0;
  for (const item of items) {
    const pieces = item.quantityStored?.value;
    // quantityStored can drift from the backend's remainder-reconciled quantityInPieces.
    if (pieces != null && usesPiecePrice(item)) {
      const piecePrice = pieceSelector(item);
      if (piecePrice == null) return null;
      rawSum += piecePrice.amount * pieces;
    } else {
      const unitPrice = standardSelector(item);
      if (unitPrice == null) return null;
      const itemTotal = unitPrice.amount * item.quantity.value;
      rawSum += isRandomTallyItem(item) ? roundToCents(itemTotal) : itemTotal;
    }
  }
  return roundToCents(rawSum);
}

export function calculateOrderTotals(
  order: OrderForTotal,
  incoTerm?: string | null
): OrderTotals {
  const listedTotal = sumOrderTotal(
    order.items,
    (item) => getListedPricePerPiece(item, incoTerm),
    (item) => {
      const listed = getListedPricePerUnit(item, incoTerm);
      return listed.amount != null ? { amount: listed.amount } : null;
    }
  );

  const suggestedTotal = sumOrderTotal(
    order.items,
    (item) => getEffectivePricePerPiece(item, incoTerm),
    (item) => item.effectivePrice
  );

  return { listedTotal, suggestedTotal };
}

/**
 * Returns the SUGGESTED total (calculateOrderTotals(order).suggestedTotal), not the listed
 * total — kept as a thin wrapper for existing call sites that only need one figure. New call
 * sites that need to distinguish listed vs. suggested should call calculateOrderTotals directly.
 */
export function calculateOrderTotal(
  order: OrderForTotal,
  incoTerm?: string | null
): number | null {
  return calculateOrderTotals(order, incoTerm).suggestedTotal;
}

/** Item shape shared by the buyer/seller order-summary cards — adds the
 * buyer-suggested price used to decide whether to show a suggested total. */
export type OrderSummaryItem = OrderItemForTotal & {
  suggestedPrice?: PricedAmount | null;
};

export type OrderSummaryTotals = OrderTotals & {
  currency: string | undefined;
  shouldShowSuggestedTotal: boolean;
};

/**
 * Shared totals + currency + "show suggested total" logic for the buyer/seller
 * order-summary cards (OrderSummaryCard / SellerOrderSummaryCard): a suggested
 * total is only surfaced while the order is awaiting seller confirmation and at
 * least one item actually carries a positive buyer-suggested price.
 */
export function getOrderSummaryTotals(
  items: OrderSummaryItem[],
  status: OrderStatus,
  currency?: string | null,
  incoTerm?: string | null
): OrderSummaryTotals {
  const { listedTotal, suggestedTotal } = calculateOrderTotals(
    { items },
    incoTerm
  );
  const shouldShowSuggestedTotal =
    status === 'PENDING_SELLER_CONFIRMATION' &&
    items.some(
      (item) => item.suggestedPrice != null && item.suggestedPrice.amount > 0
    );
  return {
    listedTotal,
    suggestedTotal,
    currency: currency ?? undefined,
    shouldShowSuggestedTotal,
  };
}

export function assertDefined<T>(
  value: T,
  message = 'Expected value to be defined'
): asserts value is NonNullable<T> {
  if (value === null || value === undefined) {
    throw new Error(message);
  }
}

export function hasReachedStatus(
  current: Exclude<OrderStatus, 'DRAFT' | 'CANCELLED' | 'BLOCKED'>,
  step: Exclude<OrderStatus, 'DRAFT' | 'CANCELLED' | 'BLOCKED'>
): boolean {
  const currentIndex = STATUS_FLOW.indexOf(current);
  const stepIndex = STATUS_FLOW.indexOf(step);

  return currentIndex >= stepIndex;
}

interface GraphQLError {
  message: string;
  locations?: Array<{ line: number; column: number }>;
  path?: string[];
  extensions?: { classification?: string; code?: string };
}

interface GraphQLResponse {
  errors?: GraphQLError[];
}

export interface ClientError extends Error {
  response?: GraphQLResponse;
}

export function generateCsvTemplate(
  headers: string[],
  filename = 'template.csv'
): void {
  downloadFile(filename, headers.join(',') + '\n', 'text/csv');
}

function escapeCsvField(field: string): string {
  if (/[",\n\r]/.test(field)) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

export function exportCsv(
  headers: string[],
  data: string[][],
  filename = 'export.csv'
): void {
  const rows = [headers, ...data].map((row) =>
    row.map(escapeCsvField).join(',')
  );
  downloadFile(filename, rows.join('\n'), 'text/csv');
}

export function extractGraphQLErrorMessages(error: ClientError): string[] {
  const fallbackMessage = error?.message || 'An unknown error occurred';

  if (error instanceof GraphQLRequestError) {
    const messages = error.errors.flatMap((err) =>
      err.message ? [err.message] : []
    );
    return messages.length > 0 ? messages : [fallbackMessage];
  }

  if (error?.response?.errors && Array.isArray(error.response.errors)) {
    const messages = error.response.errors.flatMap((err: GraphQLError) =>
      err.message ? [err.message] : []
    );

    return messages.length > 0 ? messages : [fallbackMessage];
  }

  return [fallbackMessage];
}

const NOT_FOUND_CLASSIFICATION = 'NOT_FOUND';

/**
 * Reads `extensions.classification` off each GraphQL error of a failed request.
 * The backend sets it to an `ErrorType` name (`NOT_FOUND`, `BAD_REQUEST`, ...),
 * which callers can branch on; the message text is prose and may be reworded.
 */
export function extractGraphQLErrorClassifications(
  error: ClientError
): string[] {
  return graphQLErrorsOf(error).flatMap((graphQLError) => {
    const classification = graphQLError.extensions?.classification;
    return typeof classification === 'string' ? [classification] : [];
  });
}

/**
 * Reads `extensions.code` off each GraphQL error of a failed request: a stable name for why the
 * server refused, such as `too-many-proposals`, that callers branch on instead of the message.
 */
export function extractGraphQLErrorCodes(error: ClientError): string[] {
  return graphQLErrorsOf(error).flatMap((graphQLError) => {
    const code = graphQLError.extensions?.code;
    return typeof code === 'string' ? [code] : [];
  });
}

export function findGraphQLErrorWithCode(
  error: ClientError,
  code: string
): GraphQLError | undefined {
  return graphQLErrorsOf(error).find(
    (graphQLError) => graphQLError.extensions?.code === code
  );
}

function graphQLErrorsOf(error: ClientError): readonly GraphQLError[] {
  const graphQLErrors =
    error instanceof GraphQLRequestError
      ? error.errors
      : error?.response?.errors;
  return Array.isArray(graphQLErrors) ? graphQLErrors : [];
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (value: string): boolean => UUID_PATTERN.test(value);

export function isNotFoundGraphQLError(error: ClientError): boolean {
  return extractGraphQLErrorClassifications(error).includes(
    NOT_FOUND_CLASSIFICATION
  );
}

export function toastGraphQLError(error: unknown): void {
  toast.error(extractGraphQLErrorMessages(error as ClientError).join(', '));
}

/**
 * Some external sync failures (e.g. MSR order block messages) store a raw JSON
 * ValidationProblemDetails body (ASP.NET's `{"errors": {"Field": ["message", ...]}}` shape) as
 * the message text. Flattens that into one line per error; a plain string (already a clean
 * message) passes through unchanged as a single-element array.
 */
export function formatBlockMessage(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      if (parsed.errors && typeof parsed.errors === 'object') {
        const messages = Object.values(parsed.errors as Record<string, unknown>)
          .flat()
          .filter((message): message is string => typeof message === 'string');
        if (messages.length > 0) {
          return messages;
        }
      }
      if (typeof parsed.title === 'string') {
        return [parsed.title];
      }
    }
  } catch {
    // Not JSON — already a plain message, fall through.
  }
  return [raw];
}
